import { Check, X as XIcon } from "lucide-react";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";
import { Modal } from "@/shared/ui/modal";
import { Select } from "@/shared/ui/select";

import { useSyncMulti } from "../api/use-sync-multi";
import { parseSkuList } from "../lib/parse-sku-list";

type Marketplace = "shopee" | "lazada" | "tiktok";
type SyncResult = components["schemas"]["SyncResult"];

const MKPS: Marketplace[] = ["shopee", "lazada", "tiktok"];
const MKP_LABEL: Record<Marketplace, string> = {
  shopee: "Shopee",
  lazada: "Lazada",
  tiktok: "TikTok",
};

interface SyncMultiModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fills marketplace + SKU list — used by the breaker banner's [Review] flow. */
  defaultMarketplace?: Marketplace;
  defaultSkus?: string[];
  /** Fires after a successful sync (any result), in addition to the toast. */
  onSynced?: (data: SyncResult) => void;
}

export function SyncMultiModal({
  open,
  onClose,
  defaultMarketplace,
  defaultSkus,
  onSynced,
}: SyncMultiModalProps): JSX.Element | null {
  const { t } = useTranslation("inventory");
  // Seeded once from the prefill props (e.g. the breaker banner's [Review]
  // flow) via lazy initial state — the banner forces a fresh mount (a `key`
  // tied to each Review click) when it needs a new prefill, rather than this
  // component reacting to prop changes after mount.
  const [raw, setRaw] = useState(() =>
    defaultSkus && defaultSkus.length > 0 ? defaultSkus.join("\n") : "",
  );
  const [marketplace, setMarketplace] = useState<Marketplace>(() => defaultMarketplace ?? "shopee");
  const [results, setResults] = useState<SyncResult["items"] | null>(null);
  const { mutate, isPending } = useSyncMulti();

  const skus = parseSkuList(raw);
  const canSubmit = skus.length > 0 && !isPending;

  function handleSubmit(): void {
    const targets = skus.map((sku) => ({ sku, marketplace }));
    mutate(
      { targets },
      {
        onSuccess: (data) => {
          setResults(data.items);
          const okCount = data.items.filter((i) => i.ok).length;
          const failCount = data.items.length - okCount;
          if (failCount === 0) {
            toast.success(t("sync_multi.toast_done" as never, { count: okCount }));
          } else {
            toast.warn(t("sync_multi.toast_partial" as never, { ok: okCount, failed: failCount }));
          }
          onSynced?.(data);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 429) {
            toast.warn(t("sync.rate_limited" as never));
          } else if (err instanceof ApiError && err.status === 501) {
            // Permanent until the direct-marketplace push is wired — a retry
            // can never succeed, so don't tell the user to try again.
            toast.warn(t("sync.not_available" as never));
          } else {
            toast.error(t("sync_multi.toast_error" as never));
          }
        },
      },
    );
  }

  function handleClose(): void {
    setResults(null);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      width={560}
      title={<span data-testid="sync-multi-title">{t("sync_multi.title" as never)}</span>}
      subtitle={t("sync_multi.subtitle" as never)}
      footer={
        <div className="flex w-full gap-2.5">
          <button
            type="button"
            data-testid="sync-multi-cancel"
            onClick={handleClose}
            className="flex-1 rounded-[10px] border border-[var(--border-default)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] transition hover:bg-[var(--surface-sunken)]"
          >
            {t("sync_multi.close" as never)}
          </button>
          <button
            type="button"
            data-testid="sync-multi-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="flex-1 rounded-[10px] px-4 py-2.5 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: "var(--grad-primary-btn)",
              boxShadow: "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5)",
            }}
          >
            {isPending
              ? t("sync_multi.submitting" as never)
              : t("sync_multi.submit" as never, { count: skus.length })}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Marketplace */}
        <div>
          <div className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
            {t("sync_multi.marketplace" as never)}
          </div>
          <Select
            aria-label={t("sync_multi.marketplace" as never)}
            value={marketplace}
            onChange={(e) => setMarketplace(e.target.value as Marketplace)}
            className="w-full"
            data-testid="sync-multi-marketplace"
          >
            {MKPS.map((m) => (
              <option key={m} value={m}>
                {MKP_LABEL[m]}
              </option>
            ))}
          </Select>
        </div>

        {/* SKU list */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--text-secondary)]">
              {t("sync_multi.skus_label" as never)}
            </span>
            <span className="text-xs text-[var(--text-subtle)]">
              {t("sync_multi.skus_count" as never, { count: skus.length })}
            </span>
          </div>
          <textarea
            aria-label={t("sync_multi.skus_label" as never)}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={t("sync_multi.skus_placeholder" as never)}
            rows={6}
            data-testid="sync-multi-skus"
            className="w-full resize-y rounded-[10px] border border-[var(--premium-border-strong)] px-3 py-2 font-mono text-sm outline-none focus:border-[var(--brand-500)] focus-ring"
          />
        </div>

        {/* Results */}
        {results && (
          <div
            className="overflow-hidden rounded-xl border border-[var(--premium-border)]"
            data-testid="sync-multi-results"
          >
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--row-divider)] bg-[var(--surface-sunken)]">
                  <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
                    {t("sync_multi.col_sku" as never)}
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
                    {t("sync_multi.col_result" as never)}
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--text-subtle)]">
                    {t("sync_multi.col_message" as never)}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--row-divider)]">
                {results.map((item, i) => (
                  <tr
                    key={`${item.sku}-${item.marketplace}-${i}`}
                    data-testid="sync-multi-result-row"
                  >
                    <td className="px-3 py-2 font-mono text-[13px] text-[var(--text-primary)]">
                      {item.sku}
                    </td>
                    <td className="px-3 py-2">
                      {item.ok ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-[var(--intent-success-fg)]">
                          <Check className="h-3.5 w-3.5" aria-hidden />
                          {t("sync_multi.ok" as never)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-semibold text-[var(--intent-danger-fg)]">
                          <XIcon className="h-3.5 w-3.5" aria-hidden />
                          {t("sync_multi.failed" as never)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[13px] text-[var(--text-secondary)]">
                      {item.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
